import {useMutateRepository} from '@nfq/typed-next-api';

import {contactFormService} from 'Application/services/ContactFormService';

/**
 * Custom hook for sending contact form data using the contact form service.
 * This hook provides a convenient interface for triggering contact form submissions with automatic error handling.
 * It utilizes the typed Next.js API pattern to ensure type safety throughout the mutation process.
 *
 * @returns An object containing the sendOptin function for triggering contact form submissions.
 *
 * @example
 * ```tsx
 * const ContactForm = () => {
 *     const {sendOptin} = useSendContactForm();
 *
 *     const handleSubmit = async (formData: ContactFormData) => {
 *         try {
 *             await sendOptin(formData);
 *             console.log('Contact form sent successfully');
 *         } catch (error) {
 *             console.error('Failed to send contact form:', error);
 *         }
 *     };
 *
 *     return <form onSubmit={handleSubmit}>...</form>;
 * };
 * ```
 */
export const useSendContactForm = () => {
    const {error, trigger} = useMutateRepository<typeof contactFormService.sendContactForm>(
        'sendContactForm',
        contactFormService.sendContactForm,
        {revalidate: false}
    );

    return {
        error,
        sendContactForm: trigger
    };
};